/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 19-08-2026
 *
 * Root-service voor de eenvoudige toastmelding rechtsonder (golf 3e). Vervangt
 * `$rootScope.toastdata` uit `api.service.js`: één melding tegelijk, die na een vaste tijd
 * vanzelf weer verdwijnt.
 *
 * De melding is een vertaalsleutel, geen tekst — het `dl-toast`-eiland vertaalt hem, zodat een
 * taalwissel tijdens een zichtbare toast meteen doorwerkt.
 */
import {Injectable, signal} from '@angular/core';
import {KeyedTimeout} from 'sihw-ngx';

/**
 * Hoe lang een toast blijft staan.
 */
const TOASTDUUR = 15000;

/**
 * Eén toastmelding.
 */
export interface Toast {
    /** De vertaalsleutel van de melding. */
    sleutel: string;
    /**
     * De soort melding, bijvoorbeeld `error` of `waarschuwing`. Komt als `data-soort` op het eiland
     * terecht; een melding met een soort krijgt de feedbackkleur, zonder soort is het een mededeling.
     */
    soort?: string;
}

@Injectable({providedIn: 'root'})
export class ToastService {

    /**
     * De zichtbare toast, of `null` als er niets te melden is.
     */
    private readonly _toast = signal<Toast | null>(null);

    /**
     * De timer die de toast weer opruimt.
     */
    private readonly timer = new KeyedTimeout();

    /**
     * De zichtbare toast, of `null`.
     */
    readonly toast = this._toast.asReadonly();

    /**
     * Toont een melding en ruimt hem na `TOASTDUUR` weer op. Een nieuwe melding vervangt de
     * vorige en start de tijd opnieuw.
     */
    toon(sleutel: string, soort?: string): void {
        this._toast.set({sleutel, soort});
        this.timer.set('verberg', () => this._toast.set(null), TOASTDUUR);
    }

    /**
     * Ruimt de melding meteen op.
     */
    verberg(): void {
        this.timer.clear('verberg');
        this._toast.set(null);
    }
}
